/**
 * 학원 셋업 — 학원 계정의 운영 시작 전 단계 (풋바 없는 전체 화면)
 *   ① 학원 미등록      → 온보딩 + [학원 등록하기]
 *   ② 등록함 + 승인대기 → "승인 검토 중" 안내
 *   ③ 승인됨           → /app/biz 로 이동 (학원모드 풀 기능)
 */
import React, { useState, useEffect } from 'react';
import { useNavigate, Navigate } from 'react-router-dom';
import styled from 'styled-components';
import {
  IoCheckmarkCircle, IoCard, IoCalendar, IoMegaphone, IoChatbubbles,
  IoBarChart, IoPeople, IoTime,
} from 'react-icons/io5';
import { useAcademy } from '../../context/AcademyContext';
import { useAuth } from '../../context/AuthContext';
import academyService from '../../services/academyService';
import BrandLoader from '../../components/common/BrandLoader';

/** draft에 사용자가 채운 내용이 하나라도 있는지 (빈 draft = 미시작) */
const draftHasContent = (d) => !!(d && (
  d.name || d.field || d.type || d.photoUrl ||
  d.location || d.phone || d.intro || d.regNo ||
  d.homepage || d.instagram || d.blog ||
  (d.subjects && d.subjects.length) ||
  (d.grades && d.grades.length) ||
  (d.hours && d.hours.length) ||
  (d.tuition && d.tuition.length) ||
  (d.hashtags && d.hashtags.length) ||
  (d.videos && d.videos.length) ||
  (d.promoPhotos && d.promoPhotos.length)
));

const FEATURES = [
  { Icon: IoCheckmarkCircle, color: '#16A34A', label: '출석관리' },
  { Icon: IoPeople, color: '#0891B2', label: '학생관리' },
  { Icon: IoCalendar, color: '#7C3AED', label: '수업일정' },
  { Icon: IoBarChart, color: '#0D9488', label: '성적관리' },
  { Icon: IoCard, color: '#EA580C', label: '결제관리' },
  { Icon: IoMegaphone, color: '#2563EB', label: '공지사항' },
  { Icon: IoChatbubbles, color: '#DB2777', label: '학부모소통' },
];

const BizSetupPage = () => {
  const navigate = useNavigate();
  const { userData } = useAuth();
  const { operatedAcademy, loading, loadedUid } = useAcademy();
  const [draft, setDraft] = useState(null);
  const [checked, setChecked] = useState(false);

  // 작성 중 draft 확인 — 내용 있으면 보존(이어쓰기), 빈 draft면 삭제(다시 시작)
  useEffect(() => {
    if (!userData?.uid) return undefined;
    let alive = true;
    setChecked(false);
    (async () => {
      let d = null;
      try {
        d = await academyService.getMyDraftAcademy(userData.uid);
        if (d && !draftHasContent(d)) {
          await academyService.deleteDraftAcademy(d.id);
          d = null;
        }
      } catch (e) {
        console.error('draft 확인 실패:', e);
      }
      if (alive) { setDraft(d); setChecked(true); }
    })();
    return () => { alive = false; };
  }, [userData?.uid]);

  // 내 학원 로드 + draft 확인이 끝날 때까지 대기 (화면 깜빡임 방지)
  const ready = !loading && loadedUid && loadedUid === userData?.uid && checked;
  if (!ready) return <BrandLoader fullscreen />;

  // ③ 승인 완료 → 학원모드 운영홈으로
  if (operatedAcademy && operatedAcademy.status === 'approved') {
    return <Navigate to="/app/biz" replace />;
  }

  // ② 등록함 + 승인 대기
  if (operatedAcademy) {
    return (
      <Wrap>
        <Center>
          <PendIcon><IoTime size={40} /></PendIcon>
          <Title>승인 검토 중이에요</Title>
          <Desc>
            <b>{operatedAcademy.name}</b> 등록 신청이 접수됐어요.{'\n'}
            검토 후 <b>3시간 이내</b>에 승인돼요.
          </Desc>
          <Note>승인되면 바로 학원 운영을 시작할 수 있어요</Note>
        </Center>
      </Wrap>
    );
  }

  // 작성 중인(내용 있는) draft → 등록 화면 이어쓰기
  if (draft) {
    return <Navigate to="/academy/register" replace />;
  }

  // ① 학원 미등록(또는 빈 draft 삭제됨) → 온보딩
  return (
    <Wrap>
      <Body>
        <Title>학원을 등록하고{'\n'}운영을 시작해 보세요</Title>
        <Desc>학원 정보를 구성하면{'\n'}효율적인 운영 관리를 시작할 수 있어요</Desc>

        <Tiles>
          {FEATURES.map(({ Icon, color, label }) => (
            <Tile key={label}>
              <Icon size={30} color={color} />
              <TileLabel>{label}</TileLabel>
            </Tile>
          ))}
        </Tiles>
      </Body>

      <CtaArea>
        <Cta type="button" onClick={() => navigate('/academy/register')}>
          학원 등록하기
        </Cta>
      </CtaArea>
    </Wrap>
  );
};

export default BizSetupPage;

const Wrap = styled.div`
  min-height: 100vh;
  display: flex;
  flex-direction: column;
  background: #fff;
`;

const Body = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 40px 24px 20px;
`;

const Center = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 40px 32px;
`;

const PendIcon = styled.div`
  width: 78px;
  height: 78px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #FEF3C7;
  color: #CA8A04;
  margin-bottom: 22px;
`;

const Title = styled.p`
  font-size: 23px;
  font-weight: 800;
  line-height: 1.4;
  text-align: center;
  white-space: pre-line;
  color: #1F2937;
  letter-spacing: -0.5px;
`;

const Desc = styled.p`
  margin-top: 12px;
  font-size: 15px;
  font-weight: 500;
  line-height: 1.6;
  text-align: center;
  white-space: pre-line;
  color: #6B7280;

  b { color: #4A7CFE; font-weight: 700; }
`;

const Note = styled.p`
  margin-top: 20px;
  padding: 10px 16px;
  border-radius: 999px;
  background: #F2F4F7;
  font-size: 13px;
  font-weight: 600;
  color: #6B7280;
`;

const Tiles = styled.div`
  margin-top: 30px;
  width: 100%;
  max-width: 320px;
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  gap: 10px;
`;

const Tile = styled.div`
  width: 92px;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 9px;
  padding: 18px 6px;
  border-radius: 16px;
  background: #F4F5F7;
`;

const TileLabel = styled.span`
  font-size: 13px;
  font-weight: 700;
  color: #374151;
`;

const CtaArea = styled.div`
  flex-shrink: 0;
  padding: 14px 22px calc(20px + env(safe-area-inset-bottom));
`;

const Cta = styled.button`
  width: 100%;
  height: 54px;
  border-radius: 14px;
  background: #4A7CFE;
  color: #fff;
  font-size: 16px;
  font-weight: 700;
  &:active { transform: scale(0.99); }
`;
